import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * User preferences store using Zustand
 * Centralizes user-specific settings and preferences
 */
export const useUserStore = create(
  devtools(
    persist(
      (set, get) => ({
        // User preferences
        preferences: {
          theme: 'light',
          language: 'en',
          currency: 'USD',
          timezone: 'America/New_York',
          notifications: {
            email: true,
            push: true,
            sms: false,
            marketing: false,
          },
          privacy: {
            profileVisible: true,
            showLocation: true,
            showEmail: false,
            showPhone: false,
          },
          accessibility: {
            highContrast: false,
            largeText: false,
            reduceMotion: false,
            screenReader: false,
          },
        },

        // User activity
        activity: {
          lastLogin: null,
          loginCount: 0,
          favoriteCategories: [],
          recentlyViewed: [],
          searchHistory: [],
        },

        // UI preferences
        ui: {
          sidebarCollapsed: false,
          compactMode: false,
          showTutorials: true,
          showTooltips: true,
          autoSave: true,
        },

        // Actions
        setPreference: (category, key, value) =>
          set((state) => ({
            preferences: {
              ...state.preferences,
              [category]: {
                ...state.preferences[category],
                [key]: value,
              },
            },
          })),

        setPreferences: (category, preferences) =>
          set((state) => ({
            preferences: {
              ...state.preferences,
              [category]: {
                ...state.preferences[category],
                ...preferences,
              },
            },
          })),

        setTheme: (theme) =>
          set((state) => ({
            preferences: {
              ...state.preferences,
              theme,
            },
          })),

        setLanguage: (language) =>
          set((state) => ({
            preferences: {
              ...state.preferences,
              language,
            },
          })),

        setCurrency: (currency) =>
          set((state) => ({
            preferences: {
              ...state.preferences,
              currency,
            },
          })),

        toggleNotification: (type) =>
          set((state) => ({
            preferences: {
              ...state.preferences,
              notifications: {
                ...state.preferences.notifications,
                [type]: !state.preferences.notifications[type],
              },
            },
          })),

        setPrivacySetting: (key, value) =>
          set((state) => ({
            preferences: {
              ...state.preferences,
              privacy: {
                ...state.preferences.privacy,
                [key]: value,
              },
            },
          })),

        setAccessibilitySetting: (key, value) =>
          set((state) => ({
            preferences: {
              ...state.preferences,
              accessibility: {
                ...state.preferences.accessibility,
                [key]: value,
              },
            },
          })),

        setUIState: (key, value) =>
          set((state) => ({
            ui: {
              ...state.ui,
              [key]: value,
            },
          })),

        toggleSidebar: () =>
          set((state) => ({
            ui: {
              ...state.ui,
              sidebarCollapsed: !state.ui.sidebarCollapsed,
            },
          })),

        toggleCompactMode: () =>
          set((state) => ({
            ui: {
              ...state.ui,
              compactMode: !state.ui.compactMode,
            },
          })),

        addFavoriteCategory: (category) =>
          set((state) => ({
            activity: {
              ...state.activity,
              favoriteCategories: [
                ...new Set([...state.activity.favoriteCategories, category]),
              ],
            },
          })),

        removeFavoriteCategory: (category) =>
          set((state) => ({
            activity: {
              ...state.activity,
              favoriteCategories: state.activity.favoriteCategories.filter(
                (c) => c !== category
              ),
            },
          })),

        addRecentlyViewed: (item) =>
          set((state) => ({
            activity: {
              ...state.activity,
              recentlyViewed: [
                item,
                ...state.activity.recentlyViewed.filter((i) => i.id !== item.id),
              ].slice(0, 20), // Keep only last 20 items
            },
          })),

        addSearchHistory: (query) =>
          set((state) => ({
            activity: {
              ...state.activity,
              searchHistory: [
                query,
                ...state.activity.searchHistory.filter((q) => q !== query),
              ].slice(0, 50), // Keep only last 50 searches
            },
          })),

        clearSearchHistory: () =>
          set((state) => ({
            activity: {
              ...state.activity,
              searchHistory: [],
            },
          })),

        updateLastLogin: () =>
          set((state) => ({
            activity: {
              ...state.activity,
              lastLogin: new Date().toISOString(),
              loginCount: state.activity.loginCount + 1,
            },
          })),

        // Utility functions
        getPreference: (category, key) => get().preferences[category]?.[key],
        getTheme: () => get().preferences.theme,
        getLanguage: () => get().preferences.language,
        getCurrency: () => get().preferences.currency,
        getNotificationSetting: (type) => get().preferences.notifications[type],
        getPrivacySetting: (key) => get().preferences.privacy[key],
        getAccessibilitySetting: (key) => get().preferences.accessibility[key],
        getUIState: (key) => get().ui[key],
        isSidebarCollapsed: () => get().ui.sidebarCollapsed,
        isCompactMode: () => get().ui.compactMode,
        getFavoriteCategories: () => get().activity.favoriteCategories,
        getRecentlyViewed: () => get().activity.recentlyViewed,
        getSearchHistory: () => get().activity.searchHistory,
        getLastLogin: () => get().activity.lastLogin,
        getLoginCount: () => get().activity.loginCount,

        // Reset store
        reset: () =>
          set({
            preferences: {
              theme: 'light',
              language: 'en',
              currency: 'USD',
              timezone: 'America/New_York',
              notifications: {
                email: true,
                push: true,
                sms: false,
                marketing: false,
              },
              privacy: {
                profileVisible: true,
                showLocation: true,
                showEmail: false,
                showPhone: false,
              },
              accessibility: {
                highContrast: false,
                largeText: false,
                reduceMotion: false,
                screenReader: false,
              },
            },
            activity: {
              lastLogin: null,
              loginCount: 0,
              favoriteCategories: [],
              recentlyViewed: [],
              searchHistory: [],
            },
            ui: {
              sidebarCollapsed: false,
              compactMode: false,
              showTutorials: true,
              showTooltips: true,
              autoSave: true,
            },
          }),
      }),
      {
        name: 'user-store',
        partialize: (state) => ({
          preferences: state.preferences,
          ui: state.ui,
          activity: state.activity,
        }),
      }
    ),
    {
      name: 'user-store',
    }
  )
);
